// Authenticated-user store. Replaces the pre-Slice-4 identity dropdown.
//
// Identity now flows from a server-issued auth cookie (set by /api/auth/login)
// rather than from a localStorage selector. This module is the thin React-
// facing surface: it knows how to ask the server "who am I?" and how to log
// out. The actual cookie is HttpOnly and never visible to JS.
//
// Anti-CSRF: the SPA fetches a token from /api/auth/csrf once on bootstrap
// (server sets a readable XSRF-TOKEN cookie + returns the matching token in
// the body). The token is sent back on every non-safe request as the
// X-XSRF-TOKEN header. api() handles this.

export type CurrentUser = {
  userId: string;
  email: string;
  tenantId: string;
  /**
   * True once the user has completed MFA enrollment. The top-level auth
   * gate uses this to decide between the main app and the MFA setup page.
   * Mandatory MFA means false is a valid but transient state.
   */
  mfaEnabled: boolean;
  /**
   * Platform admin flag. True for the DPAS operations team; gates the
   * Admin nav link and the /admin route in the SPA. Server-side, every
   * /api/admin endpoint is gated by the SuperAdmin policy — the SPA flag
   * is a UX hint only, not a security boundary.
   */
  isPlatformAdmin: boolean;
  /**
   * True when the user is DPAS staff (or a paying consultancy's staff).
   * Only staff can reach the audit/DPIA editor surfaces; client users
   * are read-only and land on /client/reports instead. Server-side,
   * /api/audit/* + /api/dpia/* are gated by the DpasStaff policy — the
   * SPA flag is a nav-hiding hint only.
   */
  isDpasStaff: boolean;
  /**
   * When present, this user is a <b>client</b> — a login belonging to a
   * specific ClientOrganisation. Their /client/reports view lists every
   * published report attached to that organisation. Staff users don't
   * have this field set.
   */
  clientOrganisationId: string | null;
  /**
   * Slice 15 — true when the user was created (or admin-reset) with a
   * temporary password and hasn't picked their own yet. The App gate
   * hard-redirects to /change-password until this clears, and the
   * server's MfaEnrolled / DpasStaff / ClientUser policies also refuse
   * everything sensitive while it's true.
   */
  mustChangePassword: boolean;
  /**
   * Lowercase product codes the tenant is entitled to ("audittool", "dpia").
   * Drives nav visibility - modules the tenant doesn't have are hidden in
   * the UI. The server also returns 404 on those endpoints, so the SPA
   * can't accidentally show a product that's been off-boarded.
   */
  entitledProducts: string[];
  /**
   * Server-configured idle timeout in seconds. The cookie's sliding window;
   * the SPA's local countdown mirrors it and resets on every successful
   * authenticated request via the sessionActivity pub/sub.
   */
  idleTimeoutSeconds: number;
  /**
   * How far in advance (seconds) the warning modal should fire. Sourced
   * from DpasSessionPolicy.WarningLeadTime so the SPA and server agree on
   * one number, even when the policy is tweaked. Must be < idleTimeoutSeconds
   * or the modal is always visible.
   */
  warningLeadSeconds: number;
  /**
   * Hard absolute deadline (ISO timestamp). Cookie's IssuedUtc +
   * 8 hours; sliding renewal does NOT extend it. Even an actively-used
   * session must re-auth past this point.
   */
  sessionAbsoluteExpiresAtUtc: string;
};

/** Convenience predicate over the lowercase product codes the server emits. */
export function hasProduct(user: CurrentUser, productCode: 'audittool' | 'dpia'): boolean {
  return user.entitledProducts.includes(productCode);
}

let csrfToken: string | null = null;

/**
 * Fetched lazily on the first non-safe request and cached for the session.
 *
 * The token the server issues is bound to the *current* claims principal,
 * so the cache must be dropped whenever the identity changes — see
 * {@link resetCsrfToken}, which login, MFA and logout all call.
 */
export async function ensureCsrfToken(): Promise<string> {
  if (csrfToken) return csrfToken;
  return refreshCsrfToken();
}

/**
 * Fetch a token unconditionally, replacing whatever is cached.
 *
 * Used by callers that can't afford to discover a stale token the
 * expensive way — a file upload has already put the bytes on the wire by
 * the time the server rejects the token.
 */
export async function refreshCsrfToken(): Promise<string> {
  const res = await fetch('/api/auth/csrf', { credentials: 'include' });
  if (!res.ok) throw new Error(`Failed to fetch CSRF token: ${res.status}`);
  const body = (await res.json()) as { token: string };
  csrfToken = body.token;
  return csrfToken;
}

/**
 * Drop the cached CSRF token. Call whenever the signed-in identity
 * changes — logout, but also login and MFA completion.
 *
 * Missing this on login was a latent bug until Slice 17: ASP.NET Core
 * only validates anti-forgery on endpoints that bind form data, and every
 * mutation before the evidence upload was JSON, so a token minted for the
 * anonymous pre-login principal was sent for the whole session and never
 * checked.
 */
export function resetCsrfToken(): void {
  csrfToken = null;
}
